import React from 'react';
import { ArrowRightIcon, CameraIcon } from 'lucide-react';
// Mock data
const growthPhotos = [{
  id: 1,
  month: 'January',
  image: 'https://images.unsplash.com/photo-1600804340584-c7db2eacf0bf?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
  weight: '8 lbs',
  milestone: 'First day home'
}, {
  id: 2,
  month: 'April',
  image: 'https://images.unsplash.com/photo-1583511655857-d19b40a7a54e?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
  weight: '22 lbs',
  milestone: 'First park visit'
}, {
  id: 3,
  month: 'August',
  image: 'https://images.unsplash.com/photo-1552053831-71594a27632d?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
  weight: '45 lbs',
  milestone: 'First swim'
}, {
  id: 4,
  month: 'December',
  image: 'https://images.unsplash.com/photo-1590397986332-4dd25059a5dd?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80',
  weight: '58 lbs',
  milestone: 'First snow day'
}];
interface GrowthTimelineProps {
  petName: string;
}
export const GrowthTimeline = ({
  petName
}: GrowthTimelineProps) => {
  return <section className="rounded-2xl p-5 sm:p-8 bg-white shadow-[0_10px_60px_-15px_rgba(0,0,0,0.1)] relative overflow-hidden" data-id="element-6">
      <div className="absolute top-0 right-0 w-24 sm:w-32 h-24 sm:h-32 bg-amber-100 rounded-full opacity-40 -mr-10 -mt-10" data-id="element-7"></div>
      <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8 relative" data-id="element-8">
        <div className="bg-gradient-to-br from-amber-400 to-amber-600 p-2.5 sm:p-3 rounded-xl shadow-md" data-id="element-9">
          <CameraIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" data-id="element-10" />
        </div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800" data-id="element-11">
          Growth Timeline
        </h2>
      </div>
      <p className="text-gray-700 mb-6 sm:mb-8 text-base sm:text-lg max-w-2xl" data-id="element-12">
        Watch how much {petName} has grown throughout the year!
      </p>
      {/* Mobile Timeline */}
      <div className="block sm:hidden" data-id="element-13">
        <div className="space-y-8" data-id="element-14">
          {growthPhotos.map((photo, index) => <div key={photo.id} className="flex items-center gap-4" data-id="element-15">
              <div className="relative flex-shrink-0" data-id="element-16">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-300 to-amber-500 rounded-full blur-md opacity-70 scale-110" data-id="element-17"></div>
                <img src={photo.image} alt={`${petName} in ${photo.month}`} className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-md relative z-10" data-id="element-18" />
                <div className="absolute -bottom-2 -right-2 bg-gradient-to-br from-amber-500 to-amber-700 text-white text-xs font-bold rounded-full w-9 h-9 flex items-center justify-center shadow-md z-20 leading-none" data-id="element-19">
                  {photo.weight}
                </div>
              </div>
              <div className="flex-1" data-id="element-20">
                <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-21">
                  {photo.month}
                </div>
                <div className="text-sm font-medium text-gray-600 bg-amber-50 px-3 py-1 rounded-full shadow-sm inline-block" data-id="element-22">
                  {photo.milestone}
                </div>
              </div>
            </div>)}
        </div>
      </div>
      {/* Desktop Timeline */}
      <div className="hidden sm:block relative mt-16" data-id="element-23">
        {/* Timeline line */}
        <div className="absolute top-1/2 left-0 right-0 h-2 bg-gradient-to-r from-amber-200 to-amber-400 -translate-y-1/2 z-0 rounded-full" data-id="element-24"></div>
        {/* Timeline items */}
        <div className="relative z-10 flex justify-between" data-id="element-25">
          {growthPhotos.map((photo, index) => <div key={photo.id} className="flex flex-col items-center group" data-id="element-26">
              <div className="mb-3 text-sm font-medium text-gray-500 text-center" data-id="element-27">
                {photo.month}
              </div>
              <div className="relative transform transition-all group-hover:scale-110 duration-300" data-id="element-28">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-300 to-amber-500 rounded-full blur-md opacity-70 scale-110" data-id="element-29"></div>
                <img src={photo.image} alt={`${petName} in ${photo.month}`} className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-4 border-white shadow-md relative z-10" data-id="element-30" />
                <div className="absolute -bottom-2 -right-2 bg-gradient-to-br from-amber-500 to-amber-700 text-white text-xs font-bold rounded-full w-9 h-9 md:w-10 md:h-10 flex items-center justify-center shadow-md z-20 leading-none" data-id="element-31">
                  {photo.weight}
                </div>
              </div>
              <div className="mt-4 text-center" data-id="element-32">
                <div className="text-sm font-medium text-gray-600 bg-amber-50 px-3 py-1 rounded-full shadow-sm" data-id="element-33">
                  {photo.milestone}
                </div>
              </div>
              {index < growthPhotos.length - 1 && <div className="absolute top-1/2 left-[calc(12.5%+12px+(25%*index))] -translate-y-1/2 text-amber-500 z-20" data-id="element-34">
                  <ArrowRightIcon className="w-5 h-5 md:w-6 md:h-6" data-id="element-35" />
                </div>}
            </div>)}
        </div>
      </div>
      <div className="mt-8 sm:mt-12 text-center bg-gradient-to-r from-amber-50 to-amber-100 rounded-xl p-4 shadow-sm" data-id="element-36">
        <span className="text-amber-700 font-semibold text-sm sm:text-base" data-id="element-37">
          {petName} grew from 8 lbs to 58 lbs this year!
        </span>
      </div>
    </section>;
};